// lib/nextStep.ts maps the server's one next step (models.NextStepOf, served on the products list and
// on GET /products/{pid}/next-step) to the founder's words (0051 slice 2). It decides nothing: the state, the act,
// the counts and the layer names are the server's; this file only words them. Every line of the bar and the
// row that is not the recap's (lib/runRecap) or a refusal (lib/triggerCopy) is spelled here once.

import type { components } from "../api/types";
import { buildRecap, type Recap } from "./runRecap";

export type NextStep = components["schemas"]["NextStep"];

// RULED (ruling 2): the working line.
export const WORKING_LINE = "Working on your spec. You can leave; we'll tell you when it's ready.";

// 0051 slice 2 (RULED 2026-10-07): the working state's act until the email
// keeps "we'll tell you" (a browser notification).
export const NOTIFY_ACT = "Tell me when it's ready";

// FD-1 (c).
export const TRY_AGAIN_ACT = "Try again";

// Today's pending label: the bar's Continue or Try again from the click until
// the bar reads the step its trigger started.
export const ACT_PENDING_LABEL = "starting…";

// RULED (0164 R4).
export const REVIEW_ACT = "Review them →";

// RULED (today's copy). The bare "Continue", when no layer is in progress, is
// 0051 slice 2's check wording (RULED 2026-10-07).
export function continueAct(layer: string): string {
  return layer.trim() === "" ? "Continue" : `Continue with the ${layer}`;
}

// FD-1 (c): the failed lines. "nothing charged" only when the served step says
// a committed refund paid the pass back (0159 D14).
export const FAILED_CLEAN_LINE = "Didn't finish.";
export const FAILED_CLEAN_REFUNDED_LINE = "Didn't finish, nothing charged.";

// 0051 slice 2 (RULED 2026-10-07): an idea with no pass yet.
export const NOT_STARTED_LINE = "Not started yet.";

// 0051 slice 2 (RULED 2026-10-07; 0167 Open question 1's count form): what
// holds the next pass up.
export function waitingLine(n: number): string {
  return n === 1 ? "1 thing needs you" : `${n} things need you`;
}

// 0051 slice 2 (RULED 2026-10-07; 0167 Open question 4: a quick check does not
// count in what needs you): the quiet line for questions that block nothing.
export function quickCheckLine(n: number): string {
  return n === 1
    ? "1 quick check is waiting. It doesn't hold you up."
    : `${n} quick checks are waiting. They don't hold you up.`;
}

// FD-1 (c).
export const HISTORY_LINK = "History →";

// 0051 slice 2 (RULED 2026-10-07): the bar's and the page's load-error lines,
// each with Try again.
export const BAR_LOAD_ERROR_LINE = "This didn't load.";
export const PAGE_LOAD_ERROR_LINE = "This idea didn't load.";

// 0051 slice 2 (RULED 2026-10-07): the dashboard row's form of the working line.
export const WORKING_ROW_LINE = "Working on your spec.";

// 0051 slice 2 (RULED 2026-10-07; 0167 Open question 1's header total): the
// served totals, never a count of the loaded rows.
export function dashboardTotal(ideas: number, waiting: number): string {
  const total = `${ideas} ${ideas === 1 ? "idea" : "ideas"}`;
  return waiting > 0 ? `${total} · ${waitingLine(waiting)}` : total;
}

// 0051 slice 2 (RULED 2026-10-07): the dashboard row's time line.
export function lastActiveLine(timeAgoText: string): string {
  return `last active ${timeAgoText}`;
}

// The row's time words. A timestamp ahead of this clock (the client's clock
// behind the server's) reads "today"; only a missing or unparseable one reads
// a dash.
export function timeAgo(iso: string | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return "—";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

// The bar's one act, as the server names it. The bar renders only on the
// analyzed page, so a served `start` there is an analyzed product with no pass
// record its summary can name (a legacy or imported product the backfill finds
// no pass for): its act is the same trigger the server's `start` names, worded
// as Continue, so the founder is never left without an act.
export type BarAct = { kind: "notify" | "try_again" | "review" | "continue"; label: string };

export function barAct(step: NextStep): BarAct | null {
  switch (step.act) {
    case "notify":
      return { kind: "notify", label: NOTIFY_ACT };
    case "try_again":
      return { kind: "try_again", label: TRY_AGAIN_ACT };
    case "review":
      return { kind: "review", label: REVIEW_ACT };
    case "continue":
    case "start":
      return { kind: "continue", label: continueAct(step.layer_name) };
    default:
      return null;
  }
}

// Whether the bar's act starts a pass (Continue or Try again: the page's
// trigger), the one spelling of the pair.
export function actStartsAPass(act: BarAct | null): act is BarAct & { kind: "continue" | "try_again" } {
  return act?.kind === "continue" || act?.kind === "try_again";
}

// The bar's lines for every state but the result (the bar renders the recap
// for that one). The waiting line reads the served needs_you; nothing here adds
// two counts.
export function barLines(step: NextStep): string[] {
  const counts = step.counts;
  switch (step.state) {
    case "working":
      return [WORKING_LINE];
    case "failed_clean":
      return [step.refunded ? FAILED_CLEAN_REFUNDED_LINE : FAILED_CLEAN_LINE];
    case "waiting":
      return [waitingLine(counts.needs_you)];
    case "ready_to_continue":
      return counts.quick_checks > 0 ? [quickCheckLine(counts.quick_checks)] : [];
    case "not_started":
      return [NOT_STARTED_LINE];
    case "result":
      return [];
    default:
      return [];
  }
}

// The served pass's recap: its landed counts and the layers it closed, from
// the run record through the summary, never the tab's memory.
export function recapOf(step: NextStep): Recap {
  const counts = step.counts;
  return buildRecap(
    {
      questionsAdded: counts.questions_added,
      decisionsAdded: counts.decisions_added,
      answersReturned: counts.answers_returned,
    },
    step.closed_layer_names,
    counts.set_aside,
    counts.withdrawn,
  );
}

// The dashboard row's one line: the bar's own words for the same state. A
// result reads its recap's headline (or, with none, its earlier-answer line);
// ready_to_continue has no line, only its act, so the row reads the act's
// words.
export function rowLine(step: NextStep): string {
  switch (step.state) {
    case "working":
      return WORKING_ROW_LINE;
    case "result": {
      const recap = recapOf(step);
      return recap.headline !== "" ? recap.headline : (recap.returnedAnswerLine ?? "");
    }
    case "ready_to_continue":
      return continueAct(step.layer_name);
    default:
      return barLines(step)[0] ?? "";
  }
}
